import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiClient from "../../api/apiClient.js";

export const fetchAclRules = createAsyncThunk(
  "firewall/fetchAclRules",
  async (_, { rejectWithValue }) => {
    try {
      const res = await apiClient.get("/users/firewall");
      return res.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const fetchFirewallStatus = createAsyncThunk(
  "firewall/fetchFirewallStatus",
  async (_, { rejectWithValue }) => {
    try {
      const res = await apiClient.get("/users/firewall/status");
      return res.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const createAclRule = createAsyncThunk(
  "firewall/createAclRule",
  async (rule, { rejectWithValue }) => {
    try {
      const res = await apiClient.post("/users/firewall", rule);
      return res.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.details?.join(", ") ||
          error.response?.data?.error ||
          error.message
      );
    }
  }
);

export const updateAclRule = createAsyncThunk(
  "firewall/updateAclRule",
  async ({ id, ...rule }, { rejectWithValue }) => {
    try {
      const res = await apiClient.put(`/users/firewall/${id}`, rule);
      return res.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.details?.join(", ") ||
          error.response?.data?.error ||
          error.message
      );
    }
  }
);

export const deleteAclRule = createAsyncThunk(
  "firewall/deleteAclRule",
  async (id, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/users/firewall/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const quickBlockIp = createAsyncThunk(
  "firewall/quickBlockIp",
  async ({ ip, name, description, direction = "both" }, { rejectWithValue }) => {
    try {
      const res = await apiClient.post("/users/firewall/block-ip", {
        ip,
        name,
        description,
        direction,
      });
      return res.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.details?.join(", ") ||
          error.response?.data?.error ||
          error.message
      );
    }
  }
);

export const quickUnblockIp = createAsyncThunk(
  "firewall/quickUnblockIp",
  async (ip, { rejectWithValue }) => {
    try {
      const res = await apiClient.post("/users/firewall/unblock-ip", { ip });
      return res.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.details?.join(", ") ||
          error.response?.data?.error ||
          error.message
      );
    }
  }
);

const sipAclSlice = createSlice({
  name: "firewall",
  initialState: {
    rules: [],
    loading: false,
    error: null,
    status: {
      ruleCounts: { total: 0, active: 0, deny: 0, permit: 0 },
      fail2ban: {
        enabled: false,
        currentlyBanned: 0,
        totalBanned: 0,
        currentlyFailed: 0,
        totalFailed: 0,
        bannedIps: [],
      },
      recentThreats: [],
      blockedIps: [],
    },
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAclRules.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchAclRules.fulfilled, (state, action) => { state.loading = false; state.rules = action.payload; })
      .addCase(fetchAclRules.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(fetchFirewallStatus.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchFirewallStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.status = action.payload;
      })
      .addCase(fetchFirewallStatus.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(createAclRule.pending, (state) => { state.loading = true; })
      .addCase(createAclRule.fulfilled, (state, action) => { state.loading = false; state.rules.push(action.payload); })
      .addCase(createAclRule.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(updateAclRule.pending, (state) => { state.loading = true; })
      .addCase(updateAclRule.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.rules.findIndex((r) => r.id === action.payload.id);
        if (idx !== -1) state.rules[idx] = action.payload;
      })
      .addCase(updateAclRule.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(deleteAclRule.pending, (state) => { state.loading = true; })
      .addCase(deleteAclRule.fulfilled, (state, action) => {
        state.loading = false;
        state.rules = state.rules.filter((r) => r.id !== action.payload);
      })
      .addCase(deleteAclRule.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(quickBlockIp.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(quickBlockIp.fulfilled, (state, action) => {
        state.loading = false;
        const rule = action.payload.data;
        const existingRuleIndex = state.rules.findIndex((r) => r.id === rule.id);
        if (existingRuleIndex !== -1) {
          state.rules[existingRuleIndex] = rule;
        } else if (rule?.id) {
          state.rules.push(rule);
        }
      })
      .addCase(quickBlockIp.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(quickUnblockIp.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(quickUnblockIp.fulfilled, (state, action) => {
        state.loading = false;
        const deactivatedRules = action.payload.data?.deactivatedRules || [];
        state.rules = state.rules.map((rule) =>
          deactivatedRules.includes(rule.id)
            ? { ...rule, active: false }
            : rule
        );
      })
      .addCase(quickUnblockIp.rejected, (state, action) => { state.loading = false; state.error = action.payload; });
  },
});

export default sipAclSlice.reducer;
